import PeButton from '@pe-libs/react-components/build/components/PeButton';
import Copy from '@pe-libs/react-components/build/components/PeIcons/Copy.svg';
import PeImage from '@pe-libs/react-components/build/components/PeImage';
import PeInput from '@pe-libs/react-components/build/components/PeInput';
import PeTypography from '@pe-libs/react-components/build/components/PeTypography';
import { getPublicEnvironmentValue } from '@pe-libs/react-components/build/lib/environment';
import useEventTracker from '@pe-libs/react-components/build/lib/hooks/useEventTracker';
import useIntlMessage from '@pe-libs/react-components/build/lib/intl/hooks/useIntlMessage';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import img from '#assets/images/landing/default/thankYouPage/children.png';
import { selectIsEmployerHost } from '#modules/app/selectors/settings';
import { getProfilePublicData } from '#modules/defaultLandingReferral/selectors';
import CopyLinkSuccessMessage from './CopyLinkSuccessMessage';
import Header from './Header';
import { getParameters } from './helper';
import messages from './messages';
import Socials from './Socials';
import useStyles from './styles';

const DefaultThankYouPage = () => {
  const styles = useStyles();
  const formatMessage = useIntlMessage();
  const { query } = useRouter();
  const slug = typeof query.profileName === 'string' ? query.profileName : query.profileName[0];
  const { id, profile, email, lang } = getParameters(query);
  const isEmployerHost = useSelector(selectIsEmployerHost);
  // eslint-disable-next-line max-len
  const link = `${getPublicEnvironmentValue(
    'WWW_HOST',
    isEmployerHost,
  )}/${lang}/referral/${slug}/landing-page/?id=${id}&profile=${profile}&email=${email}`;
  const linkInput = useRef<HTMLInputElement>(null);
  const data = useSelector(getProfilePublicData);
  const profileId = data.attributes.id;
  const { dispatchEvent } = useEventTracker();

  const companyName = data.attributes.title;

  const handleClick = () => {
    if (linkInput.current) {
      linkInput.current.select();
      document.execCommand('copy');

      dispatchEvent({
        from: 'referral-feature',
        type: 'referral-feature.cta.clicked',
        payload: {
          profileId,
          reviewId: id,
          emailAddress: email,
        },
        metrics: {
          itmSource: 'provenexpert',
          itmMedium: 'referral-feature-copy-button',
        },
      });

      toast.success(<CopyLinkSuccessMessage message={formatMessage(messages.copyLinkSuccess)} />, {
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      <Head>
        <title>{formatMessage(messages.SEOtitle)}</title>
        <meta name="description" content={formatMessage(messages.SEOdescription)} />
        <meta property="og:title" content={formatMessage(messages.emailSubject, { profileName: companyName })} />
        <meta property="og:description" content={formatMessage(messages.emailContent, { profileName: companyName })} />
        <meta property="og:image" content={img} />
        <meta name="robots" content="noindex" />
      </Head>
      <div className={styles.container}>
        <Header />
        <div className={styles.wrapper}>
          <div className={styles.title}>
            <PeTypography variant="h3">{formatMessage(messages.title)}</PeTypography>
            <PeTypography variant="h5" color="primary" className={styles.subtitle}>
              {formatMessage(messages.subtitle)}
            </PeTypography>
          </div>
          <div className={styles.content}>
            <div className={styles.info}>
              <PeTypography variant="body1">
                {formatMessage(messages.text, { br: <br />, company: <strong>{companyName}</strong> })}
              </PeTypography>
              <PeInput name="link" value={link} className={styles.linkInput} inputRef={linkInput} />
              <PeButton
                startIcon={<Copy />}
                color="primary"
                variant="contained"
                className={styles.button}
                onClick={handleClick}
                aria-label={`${formatMessage(messages.cta)} button`}
              >
                {formatMessage(messages.cta)}
              </PeButton>
              <Socials link={link} />
            </div>
            <div>
              <PeImage alt={formatMessage(messages.title)} src={img} layout="intrinsic" width={375} height={276} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DefaultThankYouPage;
